import styled, { DefaultTheme } from 'styled-components';
import { Card, Button, Collapse, Modal, Form, Input, Typography } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T =>
  props.theme?.mode === 'dark' ? darkValue : lightValue;

export const Container = styled.div`
  width: 100%;
  padding: 24px;
  background: ${(props) => getThemeValue(props, '#0d1117', '#f5f5f5')};

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
`;

export const Title = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 700 !important;
  letter-spacing: -0.5px;
`;

export const Subtitle = styled(Typography.Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  display: block;
  margin-top: 4px;
`;

export const PrimaryButton = styled(Button)`
  background: #2d6cca !important;
  border-color: #2d6cca !important;
  color: white !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  transition: all 0.2s;

  &:hover {
    background: #2560b8 !important;
    border-color: #2560b8 !important;
    color: white !important;
  }
`;

export const ContentCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  box-shadow: ${(props) =>
    getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};

  .ant-card-body {
    padding: 16px;
  }
`;

export const StyledCollapse = styled(Collapse)`
  background: transparent !important;
  border: none !important;

  .ant-collapse-item {
    border: none !important;
    margin-bottom: 8px;
  }

  .ant-collapse-header {
    align-items: center !important;
    padding: 12px 16px !important;
    border-radius: 8px !important;
    background: ${(props) => getThemeValue(props, '#21262d', '#fafafa')} !important;
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
    border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')} !important;

    &:hover {
      background: ${(props) => getThemeValue(props, '#30363d', '#f0f0f0')} !important;
    }
  }

  .ant-collapse-content-box {
    padding: 16px !important;
    background: ${(props) => getThemeValue(props, '#0d1117', '#ffffff')} !important;
    border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')} !important;
    border-top: none !important;
    border-radius: 0 0 8px 8px !important;
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  }

  .ant-collapse-expand-icon {
    color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  }
`;

export const ScheduleTimeRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  gap: 8px;
  color: ${(props) => getThemeValue(props, '#c9d1d9', '#333')};
  font-size: 14px;
`;

export const MetaText = styled(Typography.Text)`
  display: block;
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  margin-bottom: 4px;
`;

export const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  gap: 8px;
`;

export const EditButton = styled(Button)`
  border-radius: 8px;
`;

export const DeleteButton = styled(Button)`
  border-radius: 8px;
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 280px;
`;

export const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 280px;
  color: #ff4d4f;
  font-size: 14px;
`;

export const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
    border-radius: 12px;
  }

  .ant-modal-header {
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
    border-radius: 12px 12px 0 0;
  }

  .ant-modal-title {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
  }

  .ant-modal-body {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  }

  .ant-modal-footer {
    background: ${(props) => getThemeValue(props, '#21262d', '#fafafa')};
    border-top: 0;
    border-radius: 0 0 12px 12px;
  }
`;

export const StyledFormItem = styled(Form.Item)`
  .ant-form-item-label > label {
    color: ${(props) => getThemeValue(props, '#c9d1d9', '#1a1a1a')} !important;
  }
`;

export const StyledInput = styled(Input)`
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#d9d9d9')};
  background: ${(props) => getThemeValue(props, '#0d1117', '#ffffff')};
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
`;

export const CheckboxGroupWrapper = styled.div`
  .ant-checkbox-group {
    display: flex;
    flex-wrap: wrap;
    gap: 12px 24px;
  }

  .ant-checkbox-wrapper {
    color: ${(props) => getThemeValue(props, '#c9d1d9', '#1a1a1a')} !important;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #2d6cca;
    border-color: #2d6cca;
  }
`;

export const TitleIcon = styled.span`
  margin-right: 8px;
  display: inline-flex;
  align-items: center;
`;

export const TooltipIconWrap = styled.span`
  margin-left: 6px;
  display: inline-flex;
  align-items: center;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#999')};
  font-size: 14px;
`;

export const ModalFooterWrap = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const CancelButton = styled(Button)`
  background-color: #ededed !important;
  border: 0 !important;
  color: #121212 !important;
  border-radius: 8px;

  &:hover {
    background-color: #d9d9d9 !important;
    color: #121212 !important;
    border: 0 !important;
  }
`;
